// Local-calendar arithmetic, the browser's equivalent of Swift `Calendar.current` (WEB_PLAN.md §5.10: periods use the
// local time of the browser and the locale's first weekday). Everything works on the runtime's local time zone, so
// days, weeks and months start at local midnight and DST days are 23 or 25 hours long, as on an iPhone.

export const DAY_MS = 86_400_000;

/** `Date.distantPast` stand-in for "no lower bound" (year 1). */
export const DISTANT_PAST = new Date(-62_135_596_800_000);

/** Local midnight of the day containing `d` (`Calendar.startOfDay`). */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** `Calendar.date(byAdding: .day, value: n, to: d)`: same wall-clock time `n` calendar days later. */
export function addDays(d: Date, n: number): Date {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + n,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  );
}

/** Local midnight `n` days after the day of `d`. */
export function startOfDayOffset(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Last second of the day containing `d` (`startOfDay + 1 day - 1 s`, as `repSummary` bounds a day). */
export function endOfDay(d: Date): Date {
  return new Date(startOfDayOffset(d, 1).getTime() - 1000);
}

/** Calendar days from the day of `a` to the day of `b` (start-of-day to start-of-day; DST-safe). */
export function calendarDaysBetween(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / DAY_MS);
}

/**
 * `Calendar.dateComponents([.day], from: a, to: b).day`: whole days between two instants, where one day is the same
 * wall-clock time on the next calendar day; truncated toward zero.
 */
export function wholeDaysBetween(a: Date, b: Date): number {
  let n = calendarDaysBetween(a, b);
  if (n > 0 && addDays(a, n).getTime() > b.getTime()) n -= 1;
  else if (n < 0 && addDays(a, n).getTime() < b.getTime()) n += 1;
  return n;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** First day of the week containing `d` (`dateInterval(of: .weekOfYear)`), `firstDay` 0 = Sunday … 6 = Saturday. */
export function startOfWeek(d: Date, firstDay: number): Date {
  const diff = (d.getDay() - firstDay + 7) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Calendar quarters start in January, April, July and October. */
export function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

// CLDR supplemental weekData `firstDay` by region (the fallback when Intl.Locale has no week info, e.g. Firefox).
const SUNDAY_REGIONS = new Set(
  'AG AS BD BR BS BT BW BZ CA CN CO DM DO ET GT GU HK HN ID IL IN JM JP KE KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'.split(
    ' ',
  ),
);
const SATURDAY_REGIONS = new Set('AE AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY'.split(' '));
const FRIDAY_REGIONS = new Set(['MV']);

interface WeekInfo {
  firstDay?: number;
}

function defaultLocale(): string {
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

/**
 * The locale's first weekday as a JS day index (0 = Sunday), like iOS `Calendar.current.firstWeekday`:
 * `Intl.Locale#getWeekInfo()` (or the older `weekInfo` getter) when the browser has it, else the CLDR region table;
 * Sunday when nothing is known (en-US).
 */
export function localeWeekStart(locale?: string): number {
  try {
    const loc = new Intl.Locale(locale ?? defaultLocale());
    const withInfo = loc as Intl.Locale & { getWeekInfo?: () => WeekInfo; weekInfo?: WeekInfo };
    const info = typeof withInfo.getWeekInfo === 'function' ? withInfo.getWeekInfo() : withInfo.weekInfo;
    if (info && typeof info.firstDay === 'number' && info.firstDay >= 1 && info.firstDay <= 7) return info.firstDay % 7;
    const region = loc.maximize().region ?? 'US';
    if (SUNDAY_REGIONS.has(region)) return 0;
    if (SATURDAY_REGIONS.has(region)) return 6;
    if (FRIDAY_REGIONS.has(region)) return 5;
    return 1;
  } catch {
    return 0;
  }
}

/** Week options shared by period and report helpers: an explicit first weekday wins over the locale's. */
export interface WeekOptions {
  /** 0 = Sunday … 6 = Saturday. */
  weekStartsOn?: number;
  /** BCP 47 tag whose first weekday is used (default: the browser's language). */
  locale?: string;
}

export function resolveWeekStart(opts: WeekOptions = {}): number {
  const w = opts.weekStartsOn;
  if (typeof w === 'number' && Number.isInteger(w) && w >= 0 && w <= 6) return w;
  return localeWeekStart(opts.locale);
}
